import { Check } from 'lucide-react';
import React from 'react';

type BaseProps = React.InputHTMLAttributes<HTMLInputElement> &
  React.TextareaHTMLAttributes<HTMLTextAreaElement> &
  React.SelectHTMLAttributes<HTMLSelectElement>;
type SelectOption = string | { value: string; label: string };
interface FormFieldProps extends Omit<BaseProps, 'type'> {
  label?: string;
  prefix?: string;
  suffix?: string;
  placeholder?: string;
  type?:
    | 'text'
    | 'number'
    | 'email'
    | 'select'
    | 'textarea'
    | 'checkbox'
    | 'radio'
    | 'date'
    | 'time'
    | 'datetime';
  options?: SelectOption[];
  rows?: number;
  defaultChecked?: boolean;
  isHidden?: boolean;
}

const FormField: React.FC<FormFieldProps> = ({
  label,
  placeholder,
  type = 'text',
  prefix,
  suffix,
  options = [],
  rows = 3,
  name,
  isHidden = false,
  defaultChecked = false,
  ...rest
}) => {
  const safeId = `${name || label || type}-${Math.random()
    .toString(36)
    .slice(2, 7)}`;

  return (
    <div className="flex-1">
      {type !== 'checkbox' &&
        type !== 'radio' &&
        type !== 'datetime' &&
        label && (
          <label
            htmlFor={safeId}
            className="inline-block text-sm font-medium text-gray-700 mb-2"
          >
            {label}
          </label>
        )}

      {type === 'select' ? (
        <select id={safeId} name={name} {...rest} className="form-select-2">
          <option value="" hidden={isHidden}>
            {placeholder || 'Select an option'}
          </option>
          {options.map((option, index) => {
            if (typeof option === 'string') {
              return (
                <option key={index} value={option}>
                  {option}
                </option>
              );
            } else {
              return (
                <option key={index} value={option.value}>
                  {option.label}
                </option>
              );
            }
          })}
        </select>
      ) : type === 'textarea' ? (
        <textarea
          id={safeId}
          name={name}
          rows={rows}
          placeholder={placeholder}
          {...rest}
          className="w-full border border-light-40 rounded-2xl px-3 py-2 bg-white outline-none resize-none placeholder-light-40 focus:ring-2 focus:ring-primary text-sm sm:text-base"
        />
      ) : type === 'checkbox' ? (
        <label className="inline-flex text-gray-700 items-center gap-2 cursor-pointer">
          <input
            id={safeId}
            type="checkbox"
            name={name}
            defaultChecked={defaultChecked}
            {...rest}
            className="w-4 h-4 peer hidden"
          />
          <span className="w-5 h-5 rounded border-2 border-light-40 flex items-center justify-center peer-checked:bg-primary peer-checked:border-primary">
            <Check color="#fff" />
          </span>
          <span>{label}</span>
        </label>
      ) : type === 'radio' ? (
        <label className="inline-flex items-center gap-2 cursor-pointer relative pl-8">
          <input
            id={safeId}
            type="radio"
            name={name}
            defaultChecked={defaultChecked}
            {...rest}
            className="peer hidden"
          />
          <span className="w-6 h-6 rounded-full border-2 border-light-40 flex items-center justify-center peer-checked:border-primary absolute top-0 left-0"></span>
          <span className="w-3.5 h-3.5 rounded-full bg-primary hidden peer-checked:block absolute top-[5px] left-[5px]"></span>
          <span>{label}</span>
        </label>
      ) : type === 'datetime' ? (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label
              htmlFor={`${safeId}-date`}
              className="inline-block text-sm font-medium text-gray-700 mb-2"
            >
              {label ? `${label} Date` : 'Date'}
            </label>
            <input
              id={`${safeId}-date`}
              type="date"
              name={`${name}-date`}
              {...rest}
              className="w-full border border-light-40 rounded-2xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary h-[42px] text-sm sm:text-base"
            />
          </div>
          <div>
            <label
              htmlFor={`${safeId}-time`}
              className="inline-block text-sm font-medium text-gray-700 mb-2"
            >
              {label ? `${label} Time` : 'Time'}
            </label>
            <input
              id={`${safeId}-time`}
              type="time"
              name={`${name}-time`}
              {...rest}
              className="w-full border border-light-40 rounded-2xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary h-[42px] text-sm sm:text-base"
            />
          </div>
        </div>
      ) : (
        <div className="flex items-center border border-light-40 rounded-2xl px-3 py-2 bg-white focus-within:ring-2 focus-within:ring-primary">
          {prefix && (
            <span className="text-light-40 text-sm mr-2 border-r border-light-40 px-2">
              {prefix}
            </span>
          )}
          <input
            id={safeId}
            type={type}
            name={name}
            placeholder={placeholder}
            {...rest}
            className="flex-1 outline-none bg-transparent text-gray-700 placeholder-light-40 text-sm sm:text-base"
          />
          {suffix && <span className="text-gray-600 ml-2">{suffix}</span>}
        </div>
      )}
    </div>
  );
};

export default FormField;
